/**
 * StatementTextDisclosure — the "Show statement text" toggle on the full tab's
 * read-only cards (DDL card, generic read-only card, parse-error panel).
 * Rendered as a disclosure — chevron + label in link colour — so it reads as
 * something to click, matching the compact popover's "SQL preview" toggle
 * (`CompactSqlPreview`). It used to be muted grey text that looked static.
 *
 * The toggle and body keep the caller's testids (`<testIdPrefix>-toggle-body`
 * / `<testIdPrefix>-body`).
 */

import { useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

export interface StatementTextDisclosureProps {
  /** e.g. `vqe-readonly-fallback` → `vqe-readonly-fallback-toggle-body` / `-body`. */
  readonly testIdPrefix: string;
  /** The statement text, already highlighted or plain. */
  readonly children: ReactNode;
}

export function StatementTextDisclosure({ testIdPrefix, children }: StatementTextDisclosureProps) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-4">
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded text-xs font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`${testIdPrefix}-toggle-body`}
        aria-expanded={expanded}
      >
        <ChevronRight
          className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
          aria-hidden
        />
        {expanded ? 'Hide statement text' : 'Show statement text'}
      </button>
      {expanded ? (
        <pre
          className="mt-2 max-h-72 overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs leading-relaxed"
          data-testid={`${testIdPrefix}-body`}
        >
          {children}
        </pre>
      ) : null}
    </div>
  );
}
